import { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError, InteractionStatus, BrowserAuthError } from '@azure/msal-browser';
import { tokenRequest } from '../api/msalConfig';
import { api, setTokenProvider } from '../api/client';
import type { User } from '../types';

export function useAuth() {
  const { instance, accounts, inProgress } = useMsal();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  const account = accounts[0];

  useEffect(() => {
    // Warten bis MSAL fertig ist (Redirect verarbeitet, Token gespeichert)
    if (inProgress !== InteractionStatus.None) return;

    if (!account) {
      setLoading(false);
      return;
    }

    const getToken = async (): Promise<string> => {
      try {
        const response = await instance.acquireTokenSilent({
          ...tokenRequest,
          account,
        });
        return response.accessToken;
      } catch (err) {
        // Bei allen Interaktions-Fehlern (abgelaufenes Token, CSP-blockierter iframe, etc.)
        // → Redirect-Login auslösen. Nutzer landet nach SSO direkt zurück in der App.
        if (err instanceof InteractionRequiredAuthError || err instanceof BrowserAuthError) {
          await instance.acquireTokenRedirect({ ...tokenRequest, account });
          return '';
        }
        throw err;
      }
    };

    setTokenProvider(getToken);

    // Firma aus Microsoft Graph holen (companyName ist kein optionaler Token-Claim)
    const getCompanyName = async (): Promise<string> => {
      try {
        const graphToken = await instance.acquireTokenSilent({
          scopes: ['https://graph.microsoft.com/User.Read'],
          account,
        });
        const res = await fetch(
          'https://graph.microsoft.com/v1.0/me?$select=companyName',
          { headers: { Authorization: `Bearer ${graphToken.accessToken}` } }
        );
        if (!res.ok) return '';
        const data = await res.json() as { companyName?: string };
        return data.companyName || '';
      } catch {
        return '';
      }
    };

    const fetchUser = async (attempt = 1): Promise<void> => {
      try {
        const companyName = attempt === 1 ? await getCompanyName() : '';
        const user = await api.users.getMe(companyName);
        setCurrentUser(user);
        setApiError(false);
        setLoading(false);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : '';
        if (msg.includes('403') || msg.toLowerCase().includes('kein zugang') || msg.toLowerCase().includes('forbidden')) {
          setAccessDenied(true);
          setLoading(false);
          return;
        }
        // Einmal retry nach 2s (Kaltstart der Function App abfangen)
        if (attempt === 1) {
          setTimeout(() => fetchUser(2), 2000);
        } else {
          setApiError(true);
          setLoading(false);
        }
      }
    };

    fetchUser();
  }, [account, instance, inProgress]);

  const login = () => instance.loginRedirect({ scopes: tokenRequest.scopes });
  const logout = () => instance.logoutRedirect({ account });
  const retryApi = () => {
    setApiError(false);
    setLoading(true);
    api.users.getMe('').then((user) => {
      setCurrentUser(user);
      setLoading(false);
    }).catch(() => {
      setApiError(true);
      setLoading(false);
    });
  };

  return { currentUser, setCurrentUser, loading, isAuthenticated: !!account, apiError, accessDenied, retryApi, login, logout };
}
